import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { createSelector, select, Store } from '@ngrx/store';
import { OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { ConfigurableFormComponent } from '@ordersnproducts/modules/shared/modules/configurable-form/containers/configurable-form/configurable-form.component';
import { OnFieldChanged } from '@ordersnproducts/modules/shared/modules/configurable-form/models/on-field-changed';
import { AddedValueFieldConfig } from '../../../../models/form-added-value-field-config';
import { getOpenPlatformProducts } from '../../../../selectors/products.selectors';
import { getRmsRehydrate } from '../../../../selectors';
import { filter, map, take } from 'rxjs/operators';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { generateAddedValueFormName } from '../../../../helpers/form-added-value-name-generator';
import { LoadAddedValuesActionRMXULOP } from '../../../../actions/open-platform/added-values.actions';
import { RmsSpecificationRemoveRehydrateItem } from '../../../../actions/rms-specification.actions';
import {
  getAvailableOPVapsAndProductsRMXUL,
  getOpenPlatformProductAddedValues,
} from '../../../../selectors/added-value.selectors';

const productsAndRehydrate = createSelector(getOpenPlatformProducts, getRmsRehydrate, (products, rehydrate) => {
  return {
    products,
    rehydrate,
  };
});

const range = (n) => Array.from({ length: n }, (value, key) => key);

/**
 * Get if the added value dropdown is in rehydratation state
 * @param id
 * @param rehydrateState
 * @param config
 * @param fieldName
 * @returns boolean
 */

const isRehydratation = (
  id: number,
  rehydrateState: {
    [id: number]: {
      [key: string]: any;
    };
  },
  config: AddedValueFieldConfig,
  fieldName = config.name,
) => {
  if (rehydrateState && rehydrateState[id] && config.rehydrate) {
    const rehydratationValue = _safe(() => rehydrateState[id][fieldName]);
    return !!rehydratationValue || fieldName in rehydrateState[id];
  } else {
    return false;
  }
};

/**
 * Loads the product added values
 * This only applies the first time added values loads (To the first value added dropdown)
 */
@Injectable()
export class LoadProductAddedRMXULValues implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(
    value: string,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.store
      .select(productsAndRehydrate)
      .pipe(
        filter((state) => !!state.products && state.products.length > 0),
        take(1),
      )
      .subscribe((state) => {
        const contractCode = _safe(() => formGroup.get(SpecFieldNames.CONTRACT_SELECT).value);
        if (isRehydratation(form.data.id, state.rehydrate, config, generateAddedValueFormName(0))) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
        } else if (value) {
          this.store.dispatch(
            new LoadAddedValuesActionRMXULOP({
              productCode: value,
              index: 0,
              formId: 0,
              contractCode,
            }),
          );
        }
      });
  }
}

/**
 * Loads the added values of each added value dropdown different than the first one
 */
@Injectable()
export class LoadChildAddedValuesRMXUL implements OnFieldChanged {
  vapsBuffer = { 0: undefined, 1: undefined, 2: undefined, 3: undefined };

  constructor(private store: Store<OrderBuilderState>) {}

  private filterRehydrate(form: ConfigurableFormComponent, config: AddedValueFieldConfig) {
    return this.store.select(productsAndRehydrate).pipe(
      take(1),
      filter((rehydrateState) => {
        if (isRehydratation(form.data.id, rehydrateState, config)) {
          this.store.dispatch(new RmsSpecificationRemoveRehydrateItem({ id: form.data.id, key: config.name }));
          return false;
        } else {
          return true;
        }
      }),
    );
  }
  /**
   *Get the accumulated added values from the previous added value dropdowns
   * @param contractCode
   * @param formGroup
   * @param config
   * @returns Array of accumulated added values
   */
  private accAddedValues(contractCode: any, formGroup: UntypedFormGroup, config: AddedValueFieldConfig) {
    const keyName = `${contractCode}_${formGroup.get(SpecFieldNames.ID).value}`;
    return this.store.pipe(
      select(getOpenPlatformProductAddedValues(keyName)),
      filter((tuples) => tuples && !!tuples.length),
      take(1),
      map((tuples: any) => {
        // Accomulate added values
        return range(config.index + 1).map((_, index) => {
          const family = formGroup.get(generateAddedValueFormName(index)).value;
          if (tuples[index] && tuples[index].addedValues) {
            return tuples[index].addedValues.find((adddedValue) => adddedValue.valueAddedCode === family);
          } else {
            return null;
          }
        });
      }),
      filter((addedValue) => !!addedValue && !!addedValue.length),
    );
  }

  onFieldChanged(
    value: number,
    formGroup: UntypedFormGroup,
    form: ConfigurableFormComponent,
    config: AddedValueFieldConfig,
  ): void {
    this.store
      .select(getAvailableOPVapsAndProductsRMXUL)
      .pipe(
        filter((state) => state.availableProducts.length > 0),
        filter(
          (state) =>
            (config.index === 0 && Object.keys(state.availableAddedValues).length === 0) ||
            Object.keys(state.availableAddedValues).length > 0,
        ),
        take(1),
      )
      .subscribe(() => {
        if (value !== this.vapsBuffer[config.index]) {
          this.vapsBuffer[config.index] = value;

          this.filterRehydrate(form, config).subscribe(() => {
            // Guard
            const productCode = formGroup.get(SpecFieldNames.PRODUCT_SELECTION).value;
            const contractCode = formGroup.get(SpecFieldNames.CONTRACT_SELECT).value;
            if (config.limit || !productCode) {
              return;
            }

            // Case when unselecting first added value
            if (!value && config.index === 0) {
              this.store.dispatch(
                new LoadAddedValuesActionRMXULOP({
                  productCode: productCode,
                  index: 0,
                  formId: 0,
                  contractCode,
                }),
              );
              return;
            }

            // Get entire selected object
            this.accAddedValues(contractCode, formGroup, config).subscribe((addedValues) => {
              if (productCode) {
                // Unselect next one to avoid auto-selection when fetching finishes
                const control = formGroup.get(generateAddedValueFormName(config.index + 1));
                const userFormControl = formGroup.get(generateAddedValueFormName(config.index));
                if (userFormControl.dirty && control && control.value) {
                  control.setValue(undefined);
                }

                if (!!addedValues[0]) {
                  this.store.dispatch(
                    new LoadAddedValuesActionRMXULOP({
                      productCode: productCode,
                      index: config.index + 1,
                      addedValues: addedValues,
                      formId: 0,
                      contractCode,
                    }),
                  );
                }
              }
            });
          });
        }
        return;
      });
  }
}
